import React, {
  useRef,
  useEffect,
  useMemo,
  useCallback,
  useState,
} from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

/* ─── HELPERS ─── */
const isValidLatLng = (lat, lng) =>
  Number.isFinite(lat) && Number.isFinite(lng);

const getCurvedPoints = (start, end, customOffset) => {
  const points = [];
  const steps = 80;
  const midLat = (start[0] + end[0]) / 2;
  const midLng = (start[1] + end[1]) / 2;

  const offset = customOffset || 0.12;

  const cLat = midLat + (end[1] - start[1]) * offset;
  const cLng = midLng - (end[0] - start[0]) * offset;

  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const inv = 1 - t;
    points.push([
      inv * inv * start[0] + 2 * inv * t * cLat + t * t * end[0],
      inv * inv * start[1] + 2 * inv * t * cLng + t * t * end[1],
    ]);
  }
  return points;
};

const getDistanceInKm = (lat1, lng1, lat2, lng2) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;

  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const getBearing = (lat1, lng1, lat2, lng2) => {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const toDeg = (rad) => (rad * 180) / Math.PI;

  const dLng = toRad(lng2 - lng1);

  const y = Math.sin(dLng) * Math.cos(toRad(lat2));
  const x =
    Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
    Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLng);

  let brng = toDeg(Math.atan2(y, x));
  return (brng + 360) % 360;
};

/* ─── ANIMATED DRIVER MARKER ─── */
function AnimatedDriverMarker({ 
  targetLat, 
  targetLng, 
  icon, 
  animationDuration = 2000 
}) {
  const markerRef = useRef(null);
  const [currentPos, setCurrentPos] = useState(null);
  const animationRef = useRef(null);
  const prevTargetRef = useRef(null);

  useEffect(() => {
    // Validation
    if (!isValidLatLng(targetLat, targetLng)) return;

    const newTarget = [targetLat, targetLng];
    
    // Pehli baar - instantly set karo
    if (!currentPos || !prevTargetRef.current) {
      setCurrentPos(newTarget);
      prevTargetRef.current = newTarget;
      return;
    }

    // Agar same position hai, skip animation
    if (
      prevTargetRef.current[0] === targetLat && 
      prevTargetRef.current[1] === targetLng
    ) {
      return;
    }

    // console.log(" Driver moving:", prevTargetRef.current, "→", newTarget);

    // Animation start
    const startTime = Date.now();
    const startPos = [...currentPos];

    // Cancel previous animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / animationDuration, 1);

      // Easing function
      const easeProgress = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;

      const lat = startPos[0] + (newTarget[0] - startPos[0]) * easeProgress;
      const lng = startPos[1] + (newTarget[1] - startPos[1]) * easeProgress;

      setCurrentPos([lat, lng]);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        prevTargetRef.current = newTarget;
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [targetLat, targetLng, animationDuration]);

  if (!currentPos || !isValidLatLng(currentPos[0], currentPos[1])) {
    return null;
  }

  return <Marker ref={markerRef} position={currentPos} icon={icon} />;
}

/* ─── ZOOM CONTROLLER ─── */
function MapController({ height, bounds, minH, maxH }) {
  const map = useMap();
  const initialRef = useRef({ zoom: null, center: null, prevBounds: "" });

  useEffect(() => {
    if (!map || bounds.length === 0) return;

    const boundsKey = JSON.stringify(bounds);

    if (
      initialRef.current.zoom === null ||
      initialRef.current.prevBounds !== boundsKey
    ) {
      const b = L.latLngBounds(bounds);
      const c = b.getCenter();

      initialRef.current.center = { lat: c.lat, lng: c.lng };
      initialRef.current.zoom = map.getBoundsZoom(b) - 3.8;
      initialRef.current.prevBounds = boundsKey;
    }

    const { zoom: startZoom, center: startCenter } = initialRef.current;
    const progress = (height - minH) / (maxH - minH);

    const targetZoom = startZoom + progress * 2.5;

    const scaleFactor = Math.pow(2, targetZoom);

    const startOffset = 320;
    const endOffset = 100;
    const currentBaseOffset =
      startOffset - progress * (startOffset - endOffset);

    const dynamicLatOffset = currentBaseOffset / scaleFactor;

    const targetCenter = [
      startCenter.lat - dynamicLatOffset,
      startCenter.lng,
    ];

    map.setView(targetCenter, targetZoom, {
      animate: false,
      duration: 0,
    });
  }, [height, map, bounds, minH, maxH]);

  return null;
}

export default function OrderTrackingMap({
  pickupLat,
  pickupLng,
  homeLat,
  homeLng,
  driverLat,
  driverLng,
  restaurantIcon,
  homeIcon,
  driverIcon,
  isOrderPickedUp,
  trackingText,
  displayRemainingTime,
}) {
  const MAP_MIN = 330;
  const SCREEN_H = typeof window !== "undefined" ? window.innerHeight : 500;
  const MAP_MAX = SCREEN_H * 0.88;
  const [height, setHeight] = useState(MAP_MIN);
  const outerRef = useRef(null);
  const dragInfo = useRef({ dragging: false, startY: 0, startH: MAP_MIN });

 const bikeRotation = useMemo(() => {
  if (!isValidLatLng(driverLat, driverLng)) return 0;

  const goingToHome = trackingText?.main === "Driver is on the way";

  const targetLat = goingToHome ? homeLat : pickupLat;
  const targetLng = goingToHome ? homeLng : pickupLng;

  if (!isValidLatLng(targetLat, targetLng)) return 0;

  const baseBearing = getBearing(
    driverLat,
    driverLng,
    targetLat,
    targetLng
  );

  return goingToHome
    ? baseBearing + 90   // HOME 
    : baseBearing + 270; // rESTURANT
}, [
  driverLat,
  driverLng,
  pickupLat,
  pickupLng,
  homeLat,
  homeLng,
  trackingText,
]);


  const boundPoints = useMemo(() => {
    const pts = [];

    if (isValidLatLng(driverLat, driverLng)) {
      pts.push([driverLat, driverLng]);
    }

    const destLat = isOrderPickedUp ? homeLat : pickupLat;
    const destLng = isOrderPickedUp ? homeLng : pickupLng;

    if (isValidLatLng(destLat, destLng)) {
      pts.push([destLat, destLng]);
    }

    if (pts.length === 1) {
      const [lat, lng] = pts[0];
      pts.push([lat + 0.002, lng + 0.002]);
    }

    return pts;
  }, [
    driverLat,
    driverLng,
    pickupLat,
    pickupLng,
    homeLat,
    homeLng,
    isOrderPickedUp,
  ]);

  const { curvedStraightLine, curvedDots } = useMemo(() => {
    const pointA = [driverLat, driverLng];

    const pointB = isOrderPickedUp
      ? [homeLat, homeLng]
      : [pickupLat, pickupLng];

    if (
      !isValidLatLng(pointA[0], pointA[1]) ||
      !isValidLatLng(pointB[0], pointB[1])
    ) {
      return { curvedStraightLine: [], curvedDots: [] };
    }

    return {
      curvedStraightLine: getCurvedPoints(pointA, pointB, 0.05),
      curvedDots: getCurvedPoints(pointA, pointB, 0.18),
    };
  }, [
    driverLat,
    driverLng,
    pickupLat,
    pickupLng,
    homeLat,
    homeLng,
    isOrderPickedUp,
  ]);

  const icons = useMemo(
    () => ({
      rest:
        restaurantIcon &&
        L.divIcon({
          html: `<img src="${restaurantIcon}" style="width:45px; height:45px;"/>`,
          className: "c-icon",
          iconSize: [45, 45],
          iconAnchor: [22.5, 22.5],
        }),
      home:
        homeIcon &&
        L.divIcon({
          html: `<img src="${homeIcon}" style="width:40px; height:40px;"/>`,
          className: "c-icon",
          iconSize: [45, 45],
          iconAnchor: [22.5, 22.5],
        }),
      bike:
        driverIcon &&
        L.divIcon({
          html: `
            <img 
              src="${driverIcon}" 
              style="
                width:40px; 
                height:40px;
                transform: rotate(${bikeRotation}deg);
                transform-origin: center center;
                transition: transform 0.3s ease-out;
              "
            />
          `,
          className: "c-icon",
          iconSize: [40, 40],
          iconAnchor: [20, 20],
        }),
    }),
    [restaurantIcon, homeIcon, driverIcon, bikeRotation],
  );

  const onMove = useCallback(
    (y) => {
      if (!dragInfo.current.dragging) return;
      const delta = y - dragInfo.current.startY;
      const nextH = Math.min(
        MAP_MAX,
        Math.max(MAP_MIN, dragInfo.current.startH + delta),
      );
      setHeight(nextH);
      if (outerRef.current) outerRef.current.style.height = `${nextH}px`;
    },
    [MAP_MAX, MAP_MIN],
  );

  useEffect(() => {
    const move = (e) => {
      if (dragInfo.current.dragging) {
        if (e.cancelable) e.preventDefault();
        onMove(e.touches ? e.touches[0].clientY : e.clientY);
      }
    };
    const stop = () => {
      dragInfo.current.dragging = false;
    };

    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", stop);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", stop);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", stop);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", stop);
    };
  }, [onMove]);

  return (
    <div
      ref={outerRef}
      style={{
        width: "100%",
        height: MAP_MIN,
        position: "relative",
        overflow: "hidden",
        background: "#f1f5f9",
        touchAction: "none",
      }}
    >
      <div
        style={{ height: MAP_MAX, width: "100%", position: "absolute", top: 0 }}
      >
        <MapContainer
          center={[28.61, 77.2]}
          zoom={13}
          zoomSnap={0}
          zoomDelta={0}
          style={{ width: "100%", height: "100%" }}
          zoomControl={false}
          attributionControl={false}
          zoomAnimation={false}
          fadeAnimation={false}
          markerZoomAnimation={false}
          trackResize={false}
          inertia={false}
          updateWhenZooming={false}
          updateWhenIdle={true}
        >
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
            keepBuffer={40}
            updateWhenIdle={true}
            opacity={1}
          />

          <MapController
            height={height}
            bounds={boundPoints}
            minH={MAP_MIN}
            maxH={MAP_MAX}
          />

          {curvedStraightLine.length > 0 && (
            <Polyline
              positions={curvedStraightLine}
              pathOptions={{ color: "#333", weight: 3, opacity: 0.2 }}
            />
          )}

          {curvedDots.length > 0 && (
            <Polyline
              positions={curvedDots}
              pathOptions={{
                color: "#3b82f6",
                weight: 4,
                dashArray: "12, 10",
                lineCap: "round",
                opacity: 1,
              }}
            />
          )}

          {isValidLatLng(pickupLat, pickupLng) && (
            <Marker position={[pickupLat, pickupLng]} icon={icons.rest} />
          )}
          {isValidLatLng(homeLat, homeLng) && (
            <Marker position={[homeLat, homeLng]} icon={icons.home} />
          )}
          
          {/* ANIMATED DRIVER MARKER - Old location se new location */}
          <AnimatedDriverMarker
            targetLat={driverLat}
            targetLng={driverLng}
            icon={icons.bike}
            animationDuration={2000} 
          />
        </MapContainer>
      </div>

      {/* DRAG HANDLE */}
      <div
        onMouseDown={(e) => {
          dragInfo.current = {
            dragging: true,
            startY: e.clientY,
            startH: height,
          };
        }}
        onTouchStart={(e) => {
          dragInfo.current = {
            dragging: true,
            startY: e.touches[0].clientY,
            startH: height,
          };
        }}
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          background: "#fff",
          borderTopLeftRadius: 28,
          borderTopRightRadius: 28,
          padding: "12px 20px 40px",
          boxShadow: "0 -12px 30px rgba(0,0,0,0.12)",
          cursor: "ns-resize",
          touchAction: "none",
        }}
      >
        <div
          style={{
            width: 45,
            height: 5,
            background: "#f1f5f9",
            borderRadius: 10,
            margin: "0 auto 12px",
          }}
        />

        <div
          onMouseDown={(e) => {
            dragInfo.current = {
              dragging: true,
              startY: e.clientY,
              startH: height,
            };
          }}
          onTouchStart={(e) => {
            dragInfo.current = {
              dragging: true,
              startY: e.touches[0].clientY,
              startH: height,
            };
          }}
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            background: "#fff",
            borderTopLeftRadius: 28,
            borderTopRightRadius: 28,
            padding: "12px 20px 30px",
            boxShadow: "0 12px 30px rgba(0,0,0,0.12)",
            cursor: "ns-resize",
            touchAction: "none",
          }}
        >
          <div
            style={{
              width: 45,
              height: 5,
              background: "#f1f5f9",
              borderRadius: 10,
              margin: "0 auto 15px",
            }}
          />

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ textAlign: "left" }}>
              <p
                style={{
                  fontSize: "15px",
                  color: "#64748b",
                  margin: 0,
                  fontWeight: "600",
                }}
              >
                {trackingText?.sub}
              </p>
              <h2
                style={{
                  fontSize: "14px",
                  fontWeight: "700",
                  color: "#0f172a",
                  margin: 0,
                }}
              >
                {trackingText?.main}
              </h2>
            </div>

            <div
              style={{
                background: "#f97316",
                color: "#fff",
                width: "70px",
                height: "55px",
                borderRadius: "12px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <span
                style={{ fontSize: "18px", fontWeight: "800", lineHeight: "1" }}
              >
                {displayRemainingTime ?? "20"}
              </span>
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: "700",
                  textTransform: "uppercase",
                }}
              >
                mins
              </span>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        .leaflet-container * {
          transition: none !important;
          animation: none !important;
        }
        .leaflet-tile-container { 
          transition: none !important; 
          will-change: transform; 
        }
        .leaflet-tile { 
          opacity: 1 !important; 
          transition: none !important; 
          backface-visibility: hidden; 
          transform: translateZ(0); 
        }
        .leaflet-container { background: #f1f5f9 !important; outline: none; }
        .leaflet-pane, .leaflet-proxy, .leaflet-marker-icon, .leaflet-layer, .leaflet-zoom-animated { 
          transition: none !important; 
          animation: none !important; 
        }
        .c-icon img { display: block; filter: drop-shadow(0 4px 6px rgba(0,0,0,0.1)); }
      `}</style>
    </div>
  );
}